import { useUser } from '@ad-lucy/gateway-provider';
import {
  useBookmarkedCampaignsCountQuery,
  useFetchCampaignsCountGqlQuery,
} from '@codegen';
import {
  FilterValues,
  type TFilterValues,
} from '@modules/Campaign/Campaign.types';
import { isNonNullable } from '@modules/utils';
import { generatePath } from 'react-router-dom';
import NavBarItem from './NavBarItem';
import NavBarMenu from './NavBarMenu';
import { NavigationItem } from './nav.utils';

const ROUTES = {
  CAMPAIGNS: '/campaigns/:filter',
};

const getPath = (filter: TFilterValues) =>
  generatePath(ROUTES.CAMPAIGNS, { filter });
export function CampaignsNavBarMenu() {
  const user = useUser();
  const { loading, data } = useFetchCampaignsCountGqlQuery({
    variables: { userId: String(user.id) },
  });

  const { loading: bookmarkCountLoading, data: bookmarkedCampaignData } =
    useBookmarkedCampaignsCountQuery({
      variables: { filter: { isBookmarked: true } },
    });

  const bookmarkedCampaignCount =
    bookmarkedCampaignData?.campaignsBaseData?.fullCount ?? 0;

  const children = [
    user.require('campaign.showActive:view') ? (
      <NavBarItem
        nested={true}
        key={NavigationItem.CAMPAIGNS_ACTIVE}
        id={NavigationItem.CAMPAIGNS_ACTIVE}
        name={NavigationItem.CAMPAIGNS_ACTIVE}
        paths={[getPath(FilterValues.active)]}
        link={getPath(FilterValues.active)}
        dot={{ variant: 'active' }}
        badge={{
          loading,
          value: data?.active?.fullCount,
          variant: 'active',
        }}
      />
    ) : null,
    user.require('campaign.showDraft:view') ? (
      <NavBarItem
        nested={true}
        key={NavigationItem.CAMPAIGNS_DRAFT}
        id={NavigationItem.CAMPAIGNS_DRAFT}
        name={NavigationItem.CAMPAIGNS_DRAFT}
        paths={[getPath(FilterValues.draft)]}
        link={getPath(FilterValues.draft)}
        dot={{ variant: 'draft' }}
        badge={{
          loading,
          value: data?.draft?.fullCount,
          variant: 'draft',
        }}
      />
    ) : null,
    user.require('campaign.showArchived:view') ? (
      <NavBarItem
        nested={true}
        key={NavigationItem.CAMPAIGNS_ARCHIVED}
        id={NavigationItem.CAMPAIGNS_ARCHIVED}
        name={NavigationItem.CAMPAIGNS_ARCHIVED}
        paths={[getPath(FilterValues.archive)]}
        link={getPath(FilterValues.archive)}
        dot={{ variant: 'archived' }}
        badge={{
          loading,
          value: data?.archived?.fullCount,
        }}
      />
    ) : null,
    user.require('campaign.showMy:view') ? (
      <NavBarItem
        nested={true}
        key={NavigationItem.CAMPAIGNS_MY}
        id={NavigationItem.CAMPAIGNS_MY}
        name={NavigationItem.CAMPAIGNS_MY}
        paths={[getPath(FilterValues.my)]}
        link={getPath(FilterValues.my)}
        dot={{ variant: 'hidden' }}
        badge={{
          loading,
          value: data?.my?.fullCount,
        }}
      />
    ) : null,
    user.require('campaign.bookmark:view') ? (
      <NavBarItem
        nested={true}
        key={NavigationItem.CAMPAIGNS_BOOKMARK}
        id={NavigationItem.CAMPAIGNS_BOOKMARK}
        name={NavigationItem.CAMPAIGNS_BOOKMARK}
        paths={[getPath(FilterValues.bookmark)]}
        link={getPath(FilterValues.bookmark)}
        dot={{ variant: 'bookmark' }}
        badge={{
          loading: bookmarkCountLoading,
          value: bookmarkedCampaignCount,
        }}
      />
    ) : null,
    user.require('campaign.showAll:view') ? (
      <NavBarItem
        nested={true}
        key={NavigationItem.CAMPAIGNS_ALL}
        id={NavigationItem.CAMPAIGNS_ALL}
        name={NavigationItem.CAMPAIGNS_ALL}
        paths={['/campaigns']}
        link="/campaigns"
        dot={{ variant: 'hidden' }}
        badge={{
          loading,
          value: data?.all?.fullCount,
        }}
      />
    ) : null,
  ].filter(isNonNullable);

  const defaultLink = user.require('campaign.showActive:view')
    ? getPath(FilterValues.active)
    : getPath(FilterValues.all);
  return children.length ? (
    <NavBarItem
      id={NavigationItem.CAMPAIGNS}
      name={NavigationItem.CAMPAIGNS}
      icon="IconLayersThree"
      paths={['/campaigns/*', '/campaign/*']}
      link={defaultLink}
    >
      <NavBarMenu nested={true} itemCount={children.length}>
        {children}
      </NavBarMenu>
    </NavBarItem>
  ) : null;
}
